// src/components/editable/EditableCell.tsx
// Universal editable cell component that renders the appropriate input based on field type

import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  CircularProgress,
  Box,
  Tooltip,
  IconButton,
  InputAdornment,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  alpha,
  getLuminance,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import SearchIcon from "@mui/icons-material/Search";
import { FieldInfo, FieldChoice } from "hooks/useFieldSchema";
import { fetchData } from "api/apiHelpers";
import { athenaColors } from "../layout/AthenaDialogStyles";
import logger from "utils/logger";
import { useTranslation } from "../../i18n";

interface EditableCellProps {
  value: any;
  fieldName: string;
  fieldInfo?: FieldInfo;
  onSave: (fieldName: string, value: any) => Promise<void>;
  disabled?: boolean;
  /**
   * Optional: map of event_type slug → display category, used by the
   * inline `event_type` Autocomplete to render sub-headers. Only
   * meaningful when `fieldName === "event_type"`.
   */
  eventTypeCategories?: Record<string, string[]>;
  /**
   * Optional: resolves an event_type slug to the list of clinical fields
   * that are relevant to it. Used to compute the "fields to reset" diff
   * when the user changes event_type.
   */
  getFieldsForEventType?: (eventType: string) => string[];
  /**
   * Optional: atomic multi-field PATCH callback. When the user changes
   * `event_type` and confirms the reset, we send `{event_type: NEW, ...nulls}`
   * in a single PATCH so the backend never observes a mixed state and the
   * trust-hierarchy guard treats it as one clinical change. If absent,
   * the event_type change falls back to a normal single-field save with
   * no field-clearing.
   */
  onMultiSave?: (updates: Record<string, any>) => Promise<void>;
  /**
   * Optional: the patient id owning this row. Used only by `reference`-type
   * cells (a FK rendered as a manual picker, e.g. a Procedure's `cancer`) to
   * scope the option fetch to the patient's rows. Sourced from the row's
   * `patient` field, which every clinical row carries.
   */
  patientId?: number | string;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  fieldName,
  fieldInfo,
  onSave,
  disabled = false,
  eventTypeCategories,
  getFieldsForEventType,
  onMultiSave,
  patientId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [localValue, setLocalValue] = useState<any>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reference-field options (cursor_logs/C6): for a FK rendered as a manual
  // picker (e.g. a Procedure's `cancer` → the patient's Diagnosis rows), fetch
  // the referenced table scoped to this patient. No-op for every other cell,
  // so it stays cheap across the many non-reference cells in a table.
  const [referenceOptions, setReferenceOptions] = useState<FieldChoice[]>([]);
  const refTableType = fieldInfo?.type === "reference" ? fieldInfo.reference_table_type : undefined;
  const refLabelField = fieldInfo?.type === "reference" ? fieldInfo.reference_label_field : undefined;
  useEffect(() => {
    if (!refTableType || patientId == null) {
      setReferenceOptions([]);
      return;
    }
    let cancelled = false;
    fetchData<any>(`/${refTableType}/?patient=${patientId}`)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res) ? res : res?.results ?? [];
        setReferenceOptions(
          rows.map((r: any) => ({
            value: (r.event_id ?? r.id) as string | number,
            display_name:
              (refLabelField && r[refLabelField]) ||
              t("editable.reference_unspecified", "(unspecified)"),
          }))
        );
      })
      .catch((err) => {
        logger.error(`[EditableCell] reference option fetch failed:`, err);
      });
    return () => {
      cancelled = true;
    };
  }, [refTableType, refLabelField, patientId, t]);

  // Normalize non-primitive values (JSONField arrays/objects) to a display string.
  // MolecularProfileTable's somatic_mutations / germline_mutations are JSON arrays
  // of objects like [{gene, status, variant, protein_change, detail}].
  // React cannot render objects as children, so we stringify them.
  const normalizeForDisplay = (v: any): any => {
    if (v === null || v === undefined) return v;
    if (typeof v !== "object") return v;
    if (Array.isArray(v)) {
      // Format mutation arrays as readable text: "TP53 p.R175H (Pathogenic), PTEN Loss"
      if (v.length > 0 && typeof v[0] === "object" && v[0] !== null) {
        return v
          .map((entry: any) => {
            const parts: string[] = [];
            if (entry.gene) parts.push(entry.gene);
            if (entry.protein_change) parts.push(entry.protein_change);
            else if (entry.variant) parts.push(entry.variant);
            if (entry.status) parts.push(`(${entry.status})`);
            return parts.length > 0 ? parts.join(" ") : JSON.stringify(entry);
          })
          .join("; ");
      }
      return v.join(", ");
    }
    return JSON.stringify(v);
  };

  const normalizedValue = normalizeForDisplay(value);

  // Sync local value with prop
  useEffect(() => {
    setLocalValue(normalizedValue);
  }, [normalizedValue]);

  const handleSave = useCallback(
    async (newValue: any) => {
      if (newValue === value) return; // No change

      setSaveStatus("saving");
      setErrorMessage(null);

      try {
        await onSave(fieldName, newValue);
        setSaveStatus("saved");
        // Reset to idle after 2 seconds
        setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (err: any) {
        logger.error(`[EditableCell] Save failed for ${fieldName}:`, err);
        setSaveStatus("error");
        setErrorMessage(err.message || "Save failed");
        // Revert to original value
        setLocalValue(value);
      }
    },
    [fieldName, value, onSave]
  );

  const handleChange = useCallback(
    (newValue: any) => {
      setLocalValue(newValue);
    },
    []
  );

  const handleBlur = useCallback(() => {
    handleSave(localValue);
  }, [handleSave, localValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && fieldInfo?.type !== "text") {
        handleSave(localValue);
      }
    },
    [handleSave, localValue, fieldInfo]
  );

  // Status indicator
  const renderStatus = () => {
    if (saveStatus === "saving") {
      return <CircularProgress size={14} sx={{ ml: 0.5 }} />;
    }
    if (saveStatus === "saved") {
      return <CheckIcon sx={{ fontSize: 14, ml: 0.5, color: "success.main" }} />;
    }
    if (saveStatus === "error") {
      return (
        <Tooltip title={errorMessage || "Save failed"}>
          <ErrorIcon sx={{ fontSize: 14, ml: 0.5, color: "error.main" }} />
        </Tooltip>
      );
    }
    return null;
  };

  // ----- event_type change confirmation -------------------------------------
  // These hooks must run unconditionally on every render, before any early
  // return (otherwise React's hooks-order invariant is violated when fieldInfo
  // becomes undefined). They are no-ops when this cell is not the event_type
  // cell or when onMultiSave isn't wired in.
  const [eventTypeConfirm, setEventTypeConfirm] = useState<{
    pendingValue: string | null;
    fieldsToReset: string[];
  } | null>(null);

  const computeFieldsToReset = useCallback(
    (oldType: string | null, newType: string | null): string[] => {
      if (!getFieldsForEventType || !oldType || !newType || oldType === newType) {
        return [];
      }
      const oldRelevant = new Set(getFieldsForEventType(oldType));
      const newRelevant = new Set(getFieldsForEventType(newType));
      const toReset: string[] = [];
      for (const f of oldRelevant) {
        if (newRelevant.has(f)) continue;
        // Identity / system fields are write-protected on the backend
        // (IMMUTABLE_EVENT_FIELDS in persistor.py). Don't try to null them.
        if (f === "event_type" || f === "event_date" || f === "event_no") continue;
        toReset.push(f);
      }
      return toReset.sort();
    },
    [getFieldsForEventType]
  );

  const eventTypeGroupBy = useMemo(() => {
    if (fieldName !== "event_type" || !eventTypeCategories) return undefined;
    const slugToGroup = new Map<string, string>();
    for (const [group, slugs] of Object.entries(eventTypeCategories)) {
      for (const slug of slugs) slugToGroup.set(slug, group);
    }
    return (opt: FieldChoice | null) => {
      if (!opt || opt.value == null) return "";
      return slugToGroup.get(String(opt.value)) ?? "Other";
    };
  }, [fieldName, eventTypeCategories]);

  const commitEventTypeChange = useCallback(
    async (newType: string | null, fieldsToReset: string[]) => {
      if (onMultiSave && fieldsToReset.length > 0) {
        setSaveStatus("saving");
        setErrorMessage(null);
        try {
          const updates: Record<string, any> = { event_type: newType };
          for (const f of fieldsToReset) updates[f] = null;
          await onMultiSave(updates);
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 2000);
        } catch (err: any) {
          logger.error(
            `[EditableCell] event_type multi-save failed:`, err
          );
          setSaveStatus("error");
          setErrorMessage(err.message || "Save failed");
          setLocalValue(value);
        }
      } else {
        // No fields to reset (e.g. shared field set) or no onMultiSave wired:
        // fall back to a single-field save of event_type.
        await handleSave(newType);
      }
    },
    [onMultiSave, value, handleSave]
  );

  const requestEventTypeChange = useCallback(
    (newType: string | null) => {
      const oldType = localValue == null ? null : String(localValue);
      const toReset = computeFieldsToReset(oldType, newType);
      if (toReset.length === 0) {
        // Nothing to reset → save directly.
        setLocalValue(newType);
        if (newType !== value) handleSave(newType);
        return;
      }
      // Show confirm dialog. We do NOT update localValue yet, so the
      // dropdown stays visually consistent if the user cancels.
      setEventTypeConfirm({ pendingValue: newType, fieldsToReset: toReset });
    },
    [localValue, value, computeFieldsToReset, handleSave]
  );

  // If no field info, render as readonly text.
  // NOTE: Do not hardcode the color — let the enclosing context (EditableTable
  // on light gradient vs. FullFieldModal on dark gradient) determine the text
  // color so the text stays readable everywhere.
  if (!fieldInfo) {
    // cursor_logs/036 §18a: normalizeForDisplay only stringifies objects/arrays,
    // so a boolean/number value reaches this fallback raw. MUI's Typography
    // children PropTypes rejects raw booleans, so coerce any non-null primitive
    // to a string here. null/undefined still render as "-".
    return (
      <Typography variant="body2" sx={{ py: 0.5, fontSize: "0.8rem", color: "inherit" }}>
        {normalizedValue == null ? "-" : String(normalizedValue)}
      </Typography>
    );
  }

  // Backend-declared read-only field (e.g. SNOMED `_code` columns): keep it
  // VISIBLE but never editable, so its value can't be hand-edited out of sync
  // with the label it was derived from. Rendered with the same plain read-only
  // text styling as the no-schema fallback above. Generic — applies to any field
  // flagged `read_only`, before any type-specific input branch. Resolves a choice
  // label when the field carries choices, otherwise shows the raw value.
  if (fieldInfo.read_only) {
    const readOnlyDisplay =
      fieldInfo.choices?.find(
        (c) => (c.value ?? "") === (normalizedValue ?? "")
      )?.display_name ??
      (normalizedValue == null ? "-" : String(normalizedValue));
    return (
      <Typography variant="body2" sx={{ py: 0.5, fontSize: "0.8rem", color: "inherit" }}>
        {readOnlyDisplay}
      </Typography>
    );
  }

  const commonSx = {
    "& .MuiInputBase-input": { py: 0.5, px: 1, fontSize: "0.8rem" },
    "& .MuiOutlinedInput-root": { minHeight: "28px" },
    "& .MuiSelect-select": { fontSize: "0.8rem" },
  };

  // Joan, 2026-06-04: shared menu paper for BOTH the plain Select dropdown
  // (skin_cancer_type) and the searchable Autocomplete (primary_tumor_site).
  // The Autocomplete popper used to inherit default MUI styling that washed
  // out against the patient-details glass container; this pins both to an
  // opaque background with text.primary so contrast is identical.
  //
  // 2026-06-10 (cursor_logs/040): `theme.palette.background.paper` is actually
  // TRANSLUCENT (alpha(white, 0.04)) in both Athena themes — over the light
  // patient-details glass it reads fine, but inside the dark expand-all modal
  // (FullFieldModal) the dark backdrop bled through and dropdown text became
  // invisible (dark navy on dark). Pin an OPAQUE surface instead, chosen by the
  // luminance of `text.primary` so it contrasts in both themes (gradient = dark
  // text → light surface; classic = light text → dark surface).
  const textIsDark = getLuminance(theme.palette.text.primary) < 0.4;
  const opaqueMenuBg = textIsDark ? athenaColors.white : athenaColors.deepNavy;
  const choiceMenuPaperSx = {
    backgroundColor: opaqueMenuBg,
    backgroundImage: "none",
    color: theme.palette.text.primary,
    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
    boxShadow: theme.shadows[8],
    "& .MuiMenuItem-root, & .MuiAutocomplete-option": {
      fontSize: "0.8rem",
      minHeight: 28,
      color: theme.palette.text.primary,
      "&:hover, &.Mui-focused, &[aria-selected='true']": {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
      },
      "&.Mui-selected, &[aria-selected='true'].Mui-focused": {
        backgroundColor: alpha(theme.palette.primary.main, 0.16),
      },
    },
    // Sub-headers used by the event_type Autocomplete grouping
    "& .MuiAutocomplete-groupLabel": {
      backgroundColor: opaqueMenuBg,
      color: theme.palette.text.secondary,
      fontWeight: 600,
      fontSize: "0.72rem",
      letterSpacing: 0.4,
      textTransform: "uppercase",
    },
  };

  // Reference field (manual FK picker) — e.g. a Procedure's `cancer` linking to
  // one of the patient's Diagnosis rows. Reuses the searchable-choice Autocomplete
  // exactly. Options are the patient's referenced rows plus a "None" unassign
  // entry; the stored value is the chosen row's event_id (or null), which the
  // backend serializer accepts as the FK pk.
  if (fieldInfo.type === "reference") {
    const options: FieldChoice[] = [
      { value: null, display_name: t("editable.reference_none", "None") },
      ...referenceOptions,
    ];
    const currentOption =
      options.find((o) => (o.value ?? "") === (localValue ?? "")) ?? null;
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FormControl size="small" fullWidth sx={commonSx}>
          <Autocomplete
            size="small"
            value={currentOption}
            onChange={(_, opt) => {
              const val = opt && opt.value !== "" ? opt.value : null;
              setLocalValue(val);
              handleSave(val);
            }}
            options={options}
            getOptionLabel={(o) => o?.display_name ?? ""}
            isOptionEqualToValue={(a, b) => (a?.value ?? "") === (b?.value ?? "")}
            disabled={disabled || saveStatus === "saving"}
            autoHighlight
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={t("editable.choice_search_placeholder", "Search...")}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 0.25, mr: -0.5 }}>
                      <SearchIcon
                        sx={{ fontSize: 14, color: theme.palette.text.secondary }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: "28px",
                    fontSize: "0.8rem",
                    pr: 1,
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0.5,
                    px: 0.5,
                    fontSize: "0.8rem",
                  },
                }}
              />
            )}
            slotProps={{
              paper: { sx: choiceMenuPaperSx },
              listbox: {
                onWheelCapture: (e: React.WheelEvent) => e.stopPropagation(),
                sx: { maxHeight: 360, p: 0 },
              },
            }}
            sx={{
              "& .MuiAutocomplete-input": { fontSize: "0.8rem" },
            }}
          />
        </FormControl>
        {renderStatus()}
      </Box>
    );
  }

  // Choice field (dropdown)
  if (fieldInfo.type === "choice" && fieldInfo.choices) {
    // Check if backend already provides a null/empty option
    const hasNullOption = fieldInfo.choices.some(c => c.value === null || c.value === "");
    // A stored value that isn't in the current option list (legacy data, or the
    // value set's deliberate free-text tail) must still be DISPLAYED, never
    // hidden — otherwise a populated field renders blank and looks like lost
    // data. Surface the stored value as an extra option so the cell shows what
    // is actually there. (Joan 2026-07-15: stale "melanoma" vs "Melanoma".)
    const valueInChoices = fieldInfo.choices.some(
      (c) => (c.value ?? "") === (localValue ?? "")
    );
    const strayOptions: FieldChoice[] =
      localValue != null && localValue !== "" && !valueInChoices
        ? [{ value: localValue as string, display_name: String(localValue) }]
        : [];
    // 2026-05-25: wide choice fields (e.g. LOCATION_CHOICES, ~150 options) get
    // an Autocomplete with typeahead instead of a regular Select dropdown.
    // The threshold of 15 keeps every existing field rendering exactly as
    // before — only genuinely wide enums change behaviour. Aesthetic match
    // is via variant="standard" + the same commonSx height tweaks.
    const SEARCHABLE_THRESHOLD = 15;
    const useSearch = fieldInfo.choices.length > SEARCHABLE_THRESHOLD;

    if (useSearch) {
      // Build a stable lookup so we can map value → display label and back.
      const allowEmpty = !hasNullOption;
      const options = (allowEmpty
        ? [{ value: null as string | null, display_name: "None" } as FieldChoice]
        : []
      ).concat(fieldInfo.choices as FieldChoice[]).concat(strayOptions);

      const currentOption =
        options.find((o) => (o.value ?? "") === (localValue ?? "")) ?? null;

      const isEventType = fieldName === "event_type";
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FormControl size="small" fullWidth sx={commonSx}>
            <Autocomplete
              size="small"
              value={currentOption}
              onChange={(_, opt) => {
                const val = opt && opt.value !== "" ? opt.value : null;
                if (isEventType) {
                  // Route through the confirm-then-reset path so changing
                  // event_type doesn't carry orphan fields from the previous
                  // type. See cursor_logs/036 §13.
                  requestEventTypeChange(val == null ? null : String(val));
                  return;
                }
                setLocalValue(val);
                handleSave(val);
              }}
              options={options}
              getOptionLabel={(o) => o?.display_name ?? ""}
              groupBy={eventTypeGroupBy}
              isOptionEqualToValue={(a, b) =>
                (a?.value ?? "") === (b?.value ?? "")
              }
              disabled={disabled || saveStatus === "saving"}
              disableClearable={!allowEmpty}
              autoHighlight
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={t("editable.choice_search_placeholder", "Search...")}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 0.25, mr: -0.5 }}>
                        <SearchIcon
                          sx={{
                            fontSize: 14,
                            color: theme.palette.text.secondary,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "28px",
                      fontSize: "0.8rem",
                      pr: 1,
                    },
                    "& .MuiOutlinedInput-input": {
                      py: 0.5,
                      px: 0.5,
                      fontSize: "0.8rem",
                    },
                  }}
                />
              )}
              slotProps={{
                paper: { sx: choiceMenuPaperSx },
                // Stop wheel events at the listbox edge so scrolling inside
                // the dropdown doesn't bleed through to the editable table.
                listbox: {
                  onWheelCapture: (e: React.WheelEvent) => e.stopPropagation(),
                  sx: {
                    maxHeight: 360,
                    p: 0,
                  },
                },
              }}
              sx={{
                "& .MuiAutocomplete-input": { fontSize: "0.8rem" },
              }}
            />
          </FormControl>
          {renderStatus()}
          {eventTypeConfirm && (
            <Dialog
              open
              onClose={() => setEventTypeConfirm(null)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>
                {t(
                  "editable.event_type_change_title",
                  "Change event type?"
                )}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {t(
                    "editable.event_type_change_warning",
                    "Changing the event type will clear {{count}} field(s) that only apply to the previous type. The event date and any fields shared with the new type are preserved."
                  ).replace("{{count}}", String(eventTypeConfirm.fieldsToReset.length))}
                </DialogContentText>
                <Box
                  sx={{
                    mt: 1.5,
                    maxHeight: 200,
                    overflow: "auto",
                    p: 1,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: "0.72rem",
                  }}
                >
                  {eventTypeConfirm.fieldsToReset.join(", ")}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEventTypeConfirm(null)}>
                  {t("action.cancel", "Cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={async () => {
                    const { pendingValue, fieldsToReset } = eventTypeConfirm;
                    setEventTypeConfirm(null);
                    setLocalValue(pendingValue);
                    await commitEventTypeChange(pendingValue, fieldsToReset);
                  }}
                >
                  {t(
                    "editable.event_type_change_confirm",
                    "Change and clear fields"
                  )}
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FormControl size="small" fullWidth sx={commonSx}>
          <Select
            value={localValue ?? ""}
            onChange={(e) => {
              const val = e.target.value === "" ? null : e.target.value;
              setLocalValue(val);
              handleSave(val);
            }}
            disabled={disabled || saveStatus === "saving"}
            displayEmpty
            MenuProps={{
              PaperProps: { sx: choiceMenuPaperSx },
              MenuListProps: { sx: { p: 0 } },
            }}
          >
            {/* Only add empty option if backend doesn't provide one */}
            {!hasNullOption && (
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
            )}
            {fieldInfo.choices.map((choice) => (
              <MenuItem key={String(choice.value)} value={choice.value ?? ""}>
                {choice.display_name}
              </MenuItem>
            ))}
            {/* Stored value outside the option list — shown so it is never hidden. */}
            {strayOptions.map((choice) => (
              <MenuItem key={`stray-${String(choice.value)}`} value={choice.value ?? ""}>
                {choice.display_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {renderStatus()}
      </Box>
    );
  }

  // Boolean field (checkbox)
  if (fieldInfo.type === "boolean") {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Checkbox
          checked={localValue === true || localValue === "true"}
          onChange={(e) => {
            const val = e.target.checked;
            setLocalValue(val);
            handleSave(val);
          }}
          disabled={disabled || saveStatus === "saving"}
          size="small"
        />
        {renderStatus()}
      </Box>
    );
  }

  // Date field
  if (fieldInfo.type === "date") {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <TextField
          type="date"
          value={localValue || ""}
          // Native date pickers commit a complete value on `change` (never a
          // partial one), so save immediately — otherwise the field-save (and
          // its "edited by user" provenance badge) only fire on blur, forcing
          // the user to click elsewhere before the change registers. `onBlur`
          // stays as a no-op safety net (handleSave short-circuits when the
          // value is unchanged). This is scoped to date inputs; text/number
          // fields keep their blur/Enter debounce so we don't PATCH per keystroke.
          onChange={(e) => {
            const val = e.target.value || null;
            setLocalValue(val);
            handleSave(val);
          }}
          onBlur={handleBlur}
          disabled={disabled || saveStatus === "saving"}
          size="small"
          fullWidth
          sx={commonSx}
          InputLabelProps={{ shrink: true }}
        />
        {renderStatus()}
      </Box>
    );
  }

  // Number fields (integer, decimal)
  if (fieldInfo.type === "integer" || fieldInfo.type === "decimal") {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <TextField
          type="number"
          value={localValue ?? ""}
          onChange={(e) => {
            const val = e.target.value === "" ? null : 
              fieldInfo.type === "integer" ? parseInt(e.target.value) : parseFloat(e.target.value);
            handleChange(val);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled || saveStatus === "saving"}
          size="small"
          fullWidth
          sx={commonSx}
          inputProps={{
            step: fieldInfo.type === "decimal" ? 0.01 : 1,
          }}
        />
        {renderStatus()}
      </Box>
    );
  }

  // Text field (multiline)
  if (fieldInfo.type === "text") {
    return (
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <TextField
          value={localValue || ""}
          onChange={(e) => handleChange(e.target.value || null)}
          onBlur={handleBlur}
          disabled={disabled || saveStatus === "saving"}
          size="small"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          sx={commonSx}
        />
        {renderStatus()}
      </Box>
    );
  }

  // Default: string field
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <TextField
        value={localValue || ""}
        onChange={(e) => handleChange(e.target.value || null)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled || saveStatus === "saving"}
        size="small"
        fullWidth
        sx={commonSx}
        inputProps={{
          maxLength: fieldInfo.max_length,
        }}
      />
      {renderStatus()}
    </Box>
  );
};

export default EditableCell;
